import axios from "axios";
import config from "../../config/config";
import { buildAuthHeaders, buildAuthRequestConfig } from "../../utils/authSession";

function authHeaders() {
  return buildAuthHeaders();
}

function buildQueryString(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    query.set(key, String(value));
  });
  return query.toString();
}

function downloadBlob(blob, filename) {
  const objectUrl = window.URL.createObjectURL(new Blob([blob]));
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(objectUrl);
}

function downloadJson(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  downloadBlob(blob, filename);
}

function downloadText(text, filename, mimeType = "text/plain;charset=utf-8") {
  const blob = new Blob([text], { type: mimeType });
  downloadBlob(blob, filename);
}

async function get(url, options = {}) {
  const response = await axios.get(url, {
    ...buildAuthRequestConfig({
      ...options,
      headers: {
        ...(options.headers || {}),
        ...authHeaders(),
      },
    }),
  });
  return response.data;
}

async function post(url, body = {}, options = {}) {
  const response = await axios.post(url, body, {
    ...buildAuthRequestConfig({
      ...options,
      headers: {
        ...(options.headers || {}),
        ...authHeaders(),
      },
    }),
  });
  return response.data;
}

async function patch(url, body = {}, options = {}) {
  const response = await axios.patch(url, body, {
    ...buildAuthRequestConfig({
      ...options,
      headers: {
        ...(options.headers || {}),
        ...authHeaders(),
      },
    }),
  });
  return response.data;
}

async function remove(url, options = {}) {
  const response = await axios.delete(url, {
    ...buildAuthRequestConfig({
      ...options,
      headers: {
        ...(options.headers || {}),
        ...authHeaders(),
      },
    }),
  });
  return response.data;
}

export async function listEmployerTrainingScenarios() {
  const data = await get(`${config.API_BASE_URL}/api/employer/training/scenarios`);
  return data?.scenarios || [];
}

export async function getEmployerTrainingOverview() {
  return get(`${config.API_BASE_URL}/api/employer/training/current`);
}

export async function getCurrentEmployerTrainingRun() {
  const data = await getEmployerTrainingOverview();
  return data?.current || null;
}

export async function startEmployerTrainingRun({ jobAdId, scenarioKey }) {
  const data = await post(`${config.API_BASE_URL}/api/employer/training/start`, {
    job_ad_id: jobAdId,
    scenario_key: scenarioKey,
  });
  return data?.run || null;
}

export async function restartEmployerTrainingRun({ runId, scenarioKey }) {
  const data = await post(`${config.API_BASE_URL}/api/employer/training/runs/${runId}/restart`, {
    scenario_key: scenarioKey,
  });
  return data?.run || null;
}

export async function endEmployerTrainingRun(runId) {
  const data = await post(`${config.API_BASE_URL}/api/employer/training/runs/${runId}/end`, {});
  return data?.run || null;
}

export async function queryEmployerTrainingCoach({ runId, question, quickAction, surfaceContext }) {
  const data = await post(`${config.API_BASE_URL}/api/employer/training/coach/query`, {
    run_id: runId,
    question,
    quick_action: quickAction || "",
    surface_context: surfaceContext || {},
  });
  return {
    reply: data?.reply || null,
    run: data?.run || null,
  };
}

export async function queryEmployerLiveCoach({ question, quickAction, surfaceContext }) {
  const data = await post(`${config.API_BASE_URL}/api/employer/coach/query`, {
    question,
    quick_action: quickAction || "",
    surface_context: surfaceContext || {},
  });
  return {
    reply: data?.reply || null,
    coachContext: data?.coach_context || null,
  };
}

export async function getEmployerProfile() {
  const data = await get(`${config.API_BASE_URL}/api/employer/profile`);
  return data?.profile || null;
}

export async function saveEmployerProfile(profile) {
  const data = await axios.put(`${config.API_BASE_URL}/api/employer/profile`, profile || {}, {
    ...buildAuthRequestConfig({ headers: authHeaders() }),
  });
  return data?.data?.profile || null;
}

export async function updateEmployerTrustReviewArea(areaKey, payload) {
  const data = await patch(`${config.API_BASE_URL}/api/employer/profile/trust/review-areas/${areaKey}`, payload || {});
  return data?.profile || null;
}

export async function updateEmployerTrustReviewTask(taskId, payload) {
  const data = await patch(`${config.API_BASE_URL}/api/employer/profile/trust/review-tasks/${taskId}`, payload || {});
  return data?.profile || null;
}

export async function createEmployerTrustIncident(payload) {
  const data = await post(`${config.API_BASE_URL}/api/employer/profile/trust/incidents`, payload || {});
  return data?.profile || null;
}

export async function updateEmployerTrustIncident(incidentId, payload) {
  const data = await patch(`${config.API_BASE_URL}/api/employer/profile/trust/incidents/${incidentId}`, payload || {});
  return data?.profile || null;
}

export async function runEmployerTrustRealityCheck(payload) {
  const data = await post(`${config.API_BASE_URL}/api/employer/profile/trust/reality-check`, payload || {});
  return data?.profile || null;
}

function createLiveAdapter() {
  return {
    kind: "live",
    async loadAds(params = {}) {
      return get(`${config.API_BASE_URL}/api/stb/employer/ads`, { params });
    },
    async runAdAction(adId, action) {
      if (action === "duplicate") return post(`${config.API_BASE_URL}/api/stb/employer/ads/${adId}/duplicate`, {});
      if (action === "publish") return post(`${config.API_BASE_URL}/api/stb/employer/ads/${adId}/publish`, {});
      if (action === "pause") return post(`${config.API_BASE_URL}/api/stb/employer/ads/${adId}/pause`, {});
      if (action === "resume") return post(`${config.API_BASE_URL}/api/stb/employer/ads/${adId}/resume`, {});
      if (action === "archive") return post(`${config.API_BASE_URL}/api/stb/employer/ads/${adId}/archive`, {});
      if (action === "delete") return remove(`${config.API_BASE_URL}/api/stb/employer/ads/${adId}`);
      return null;
    },
    async loadApplications({ adId }) {
      return get(`${config.API_BASE_URL}/api/stb/employer/ads/${adId}/applications`);
    },
    async loadDecisionSummary({ applicationId }) {
      return get(`${config.API_BASE_URL}/api/stb/employer/applications/${applicationId}/decision-handoff`);
    },
    async saveDecisionPreparation({ applicationId, form }) {
      return patch(`${config.API_BASE_URL}/api/stb/employer/applications/${applicationId}/decision-preparation`, form);
    },
    async loadAuthorityFilingKit({ applicationId, filingForm }) {
      const query = buildQueryString(filingForm);
      return get(`${config.API_BASE_URL}/api/stb/employer/applications/${applicationId}/authority-filing-kit${query ? `?${query}` : ""}`);
    },
    async saveAuthorityFilingProfile({ applicationId, filingForm }) {
      return patch(`${config.API_BASE_URL}/api/stb/employer/applications/${applicationId}/authority-filing-kit/profile`, filingForm || {});
    },
    async exportAuthorityFilingKit({ applicationId, filingForm, format, filename }) {
      const query = buildQueryString({ ...filingForm, format });
      const response = await axios.get(
        `${config.API_BASE_URL}/api/stb/employer/applications/${applicationId}/authority-filing-kit/export?${query}`,
        {
          ...buildAuthRequestConfig({ headers: authHeaders() }),
          responseType: "blob",
        }
      );
      downloadBlob(response.data, filename);
      return true;
    },
    async downloadAuthorityDocument({ applicationId, documentEntry }) {
      if (!documentEntry?.download_url) return false;
      const filename = documentEntry.local_path?.split("/").pop() || `${documentEntry.id}.pdf`;
      const response = await axios.get(`${config.API_BASE_URL}${documentEntry.download_url}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      downloadBlob(response.data, filename);
      return true;
    },
    async downloadPreparedAsset({ applicationId, assetEntry }) {
      if (!assetEntry?.download_url) return false;
      const response = await axios.get(`${config.API_BASE_URL}${assetEntry.download_url}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      downloadBlob(response.data, assetEntry.file_name || `prepared-asset-${applicationId}.txt`);
      return true;
    },
    async updateAuthorityTask({ applicationId, taskId, action, completionNote, filingForm }) {
      const query = buildQueryString(filingForm);
      return patch(
        `${config.API_BASE_URL}/api/stb/employer/applications/${applicationId}/authority-filing-kit/tasks/${taskId}${query ? `?${query}` : ""}`,
        {
          action,
          completion_note: completionNote || "",
        }
      );
    },
    async createCandidateRequest({ applicationId, task, sendNow, filingForm }) {
      const query = buildQueryString(filingForm);
      return post(
        `${config.API_BASE_URL}/api/stb/employer/applications/${applicationId}/authority-filing-kit/requests${query ? `?${query}` : ""}`,
        {
          task_id: task.task_id,
          document_id: task.document_id,
          title: `Request: ${task.title}`,
          description: task.description || "Bitte fehlende Angabe oder Unterlage fuer das Filing Kit bereitstellen.",
          request_reason: task.description || task.title,
          requirement_kind: task.source_type,
          urgency: task.priority || "medium",
          requires_upload: task.requires_upload || task.requires_candidate_input,
          requires_signature: task.requires_signature,
          due_at: task.due_at || null,
          status: sendNow ? "sent" : "draft",
          send_now: sendNow,
        }
      );
    },
    async updateCandidateRequest({ applicationId, requestId, status, extra, filingForm }) {
      const query = buildQueryString(filingForm);
      return patch(
        `${config.API_BASE_URL}/api/stb/employer/applications/${applicationId}/authority-filing-kit/requests/${requestId}${query ? `?${query}` : ""}`,
        { status, ...(extra || {}) }
      );
    },
    async runPackAction({ applicationId, action, packId, filingForm }) {
      const query = buildQueryString(filingForm);
      return post(
        `${config.API_BASE_URL}/api/stb/employer/applications/${applicationId}/authority-filing-kit/pack-actions${query ? `?${query}` : ""}`,
        { action, pack_id: packId || null }
      );
    },
    async refreshAuthorityDocuments({ applicationId, filingForm }) {
      const query = buildQueryString(filingForm);
      return post(`${config.API_BASE_URL}/api/stb/employer/applications/${applicationId}/authority-filing-kit/refresh-documents${query ? `?${query}` : ""}`, {});
    },
  };
}

function createTrainingAdapter(trainingRun) {
  const runId = trainingRun?.id;
  return {
    kind: "training",
    runId,
    async loadAds(params = {}) {
      const live = await get(`${config.API_BASE_URL}/api/stb/employer/ads`, { params });
      const rows = (live?.rows || []).map((entry) => ({
        ...entry,
        training_anchor: Number(trainingRun?.job_ad_snapshot?.id) === Number(entry.id),
      }));
      return {
        ...live,
        rows,
      };
    },
    async runAdAction() {
      return { ok: true, simulated: true };
    },
    async loadApplications({ adId }) {
      return get(`${config.API_BASE_URL}/api/employer/training/runs/${runId}/ads/${adId}/applications`);
    },
    async loadDecisionSummary({ applicationId }) {
      return get(`${config.API_BASE_URL}/api/employer/training/runs/${runId}/applications/${applicationId}/decision-handoff`);
    },
    async saveDecisionPreparation({ applicationId, form }) {
      return patch(`${config.API_BASE_URL}/api/employer/training/runs/${runId}/applications/${applicationId}/decision-preparation`, form);
    },
    async loadAuthorityFilingKit({ applicationId }) {
      return get(`${config.API_BASE_URL}/api/employer/training/runs/${runId}/applications/${applicationId}/authority-filing-kit`);
    },
    async saveAuthorityFilingProfile({ filingForm }) {
      return {
        input_profile: filingForm || {},
        classification: null,
        simulated: true,
      };
    },
    async exportAuthorityFilingKit({ applicationId, format, filename, filingForm }) {
      const kit = await this.loadAuthorityFilingKit({ applicationId, filingForm });
      if (format === "html") {
        downloadText(
          `<html><body><pre>${JSON.stringify(kit, null, 2)}</pre></body></html>`,
          filename,
          "text/html;charset=utf-8"
        );
      } else {
        downloadJson(kit, filename);
      }
      return true;
    },
    async downloadAuthorityDocument({ applicationId, documentEntry }) {
      downloadText(
        `Trainingsdokument: ${documentEntry?.title || "Dokument"}\nKein echter Download, keine operative Datei.`,
        `training-document-${applicationId}-${documentEntry?.id || "item"}.txt`
      );
      return true;
    },
    async downloadPreparedAsset({ applicationId, assetEntry }) {
      downloadText(
        `Trainingsasset fuer Fall ${applicationId}\nAsset: ${assetEntry?.file_name || "prepared-asset"}\nKeine echte Zustellung.`,
        assetEntry?.file_name || `training-asset-${applicationId}.txt`
      );
      return true;
    },
    async updateAuthorityTask({ applicationId, taskId, action, completionNote }) {
      return patch(
        `${config.API_BASE_URL}/api/employer/training/runs/${runId}/applications/${applicationId}/authority-filing-kit/tasks/${taskId}`,
        {
          action,
          completion_note: completionNote || "",
        }
      );
    },
    async createCandidateRequest({ applicationId, task, sendNow }) {
      return post(
        `${config.API_BASE_URL}/api/employer/training/runs/${runId}/applications/${applicationId}/authority-filing-kit/requests`,
        {
          task_id: task.task_id,
          send_now: sendNow,
        }
      );
    },
    async updateCandidateRequest({ applicationId, requestId, status, extra }) {
      return patch(
        `${config.API_BASE_URL}/api/employer/training/runs/${runId}/applications/${applicationId}/authority-filing-kit/requests/${requestId}`,
        {
          status,
          ...(extra || {}),
        }
      );
    },
    async runPackAction({ applicationId, action, packId }) {
      return post(
        `${config.API_BASE_URL}/api/employer/training/runs/${runId}/applications/${applicationId}/authority-filing-kit/pack-actions`,
        {
          action,
          pack_id: packId || null,
        }
      );
    },
    async refreshAuthorityDocuments({ applicationId }) {
      return post(`${config.API_BASE_URL}/api/employer/training/runs/${runId}/applications/${applicationId}/authority-filing-kit/refresh-documents`, {});
    },
  };
}

export function createEmployerWorkspaceAdapter({ mode, trainingRun }) {
  return mode === "training" && trainingRun ? createTrainingAdapter(trainingRun) : createLiveAdapter();
}
